import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Wrench } from "lucide-react";
import { api, saveToken, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Kein Token gefunden");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
      });
      saveToken(r.data.token);
      toast.success("Passwort zurückgesetzt");
      nav("/admin");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="flex min-h-screen items-center justify-center px-6 bg-grid"
    >
      <div className="w-full max-w-md border border-white/10 bg-[#0d0d10] p-10">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center bg-[#FF4B00]">
            <Wrench className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <span className="font-[Chivo] text-sm font-black uppercase tracking-widest">
            Admin
          </span>
        </Link>

        <div className="mt-10">
          <span className="tag-orange">Neues Passwort</span>
          <h1 className="mt-4 text-3xl font-black uppercase">Passwort setzen</h1>
          <p className="mt-2 text-sm text-white/60">
            Wählen Sie ein neues, sicheres Passwort für Ihr Admin-Konto.
          </p>
        </div>

        {!token && (
          <div className="mt-8 border border-[#EF4444] bg-[#EF4444]/10 p-5">
            <p className="text-sm text-white">
              Kein gültiger Reset-Token in der URL. Bitte fordern Sie einen
              neuen Link an.
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
              Neues Passwort
            </Label>
            <Input
              data-testid="reset-password"
              type="password"
              className="mono mt-2 border-white/20 bg-black text-white"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
            />
            <p className="mono mt-2 text-[10px] uppercase tracking-widest text-white/40">
              Mindestens 6 Zeichen
            </p>
          </div>
          <div>
            <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
              Passwort bestätigen
            </Label>
            <Input
              data-testid="reset-confirm"
              type="password"
              className="mono mt-2 border-white/20 bg-black text-white"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          <button
            data-testid="reset-submit"
            type="submit"
            disabled={loading || !token}
            className="mt-2 inline-flex w-full items-center justify-center gap-2 bg-[#FF4B00] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E64300] disabled:opacity-40"
          >
            <KeyRound className="h-4 w-4" strokeWidth={2.5} />
            {loading ? "Setze zurück..." : "Passwort speichern"}
          </button>
        </form>
      </div>
    </div>
  );
}
